import {
  buildAnrokIntegrationEntityWithMetadata,
  findIntegrationByProviderInEntities,
  isFixedProduct,
} from '@vy/services';
import { INTEGRATION_PROVIDERS, type AnrokProductEntity, type FixedProduct, type UsageProduct } from '@vy/types';
import { isEmptyString } from '@vy/utils';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { withSuspenseBoundary } from '../../../../../../components/utility/SuspenseBoundary';
import { useFormErrorNotificationContext } from '../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { useChargeContext } from '../../charge-context';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { VyTextField } from '@vy/ui-design-system';

const ProductAnrokSelectorInner: React.FC = () => {
  const { itemIndex: productIndex, item: product } = useChargeContext<FixedProduct | UsageProduct>();
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();
  const { setValue, watch } = useFormContext<PlanInputForm>();

  const productType = isFixedProduct(product) ? 'fixedProducts' : 'usageProducts';
  const integrationEntitiesField = `${productType}.${productIndex}.integrationEntities` as const;
  const integrationEntities = watch(integrationEntitiesField);
  const anrokIntegrationEntity = findIntegrationByProviderInEntities(
    integrationEntities ?? [],
    INTEGRATION_PROVIDERS.anrok,
  );

  useEffect(() => {
    registerErrorField(integrationEntitiesField, CREATE_PLAN_TABS.serviceFees);
  }, [registerErrorField, integrationEntitiesField]);

  const handleAnrokIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const productId = event.target.value;
    const currentEntities = integrationEntities ?? [];

    // Empty field means "not set" — drop the Anrok entity entirely (matches the legacy toggle-off behavior).
    if (isEmptyString(productId)) {
      const newIntegrationEntities = currentEntities.filter(
        (entity) => entity.integrationProvider !== INTEGRATION_PROVIDERS.anrok,
      );
      setValue(integrationEntitiesField, newIntegrationEntities, { shouldDirty: true });
      return;
    }

    const anrokIndex = currentEntities.findIndex(
      (entity) => entity.integrationProvider === INTEGRATION_PROVIDERS.anrok,
    );

    const updatedEntities =
      anrokIndex === -1
        ? [...currentEntities, buildAnrokIntegrationEntityWithMetadata(productId)]
        : currentEntities.map((entity, index) =>
            index !== anrokIndex ? entity : buildAnrokIntegrationEntityWithMetadata(productId),
          );

    setValue(integrationEntitiesField, updatedEntities, { shouldDirty: true });
  };

  const anrokExternalId = (anrokIntegrationEntity as AnrokProductEntity | undefined)?.integrationExternalId ?? '';

  return (
    <VyTextField
      data-vy="plan-charge-drawer-anrok-input"
      label="Anrok Product ID"
      value={anrokExternalId}
      onChange={handleAnrokIdChange}
      placeholder="Enter Anrok Product ID"
      fullWidth
    />
  );
};

const ProductAnrokSelectorWithSuspenseBoundary = withSuspenseBoundary(ProductAnrokSelectorInner);

export { ProductAnrokSelectorWithSuspenseBoundary as ProductAnrokSelector };
